import React, { useState, useEffect } from 'react';
import {
    getPools, getRounds,
    createPool, updatePoolSettings, updateScoringRules, getScoringRules
} from '../services/api';

const POOL_TYPES    = ['bracket', 'round_by_round'];
const SCORING_TYPES = ['flat', 'multiplier'];

function AdminPoolConfig({ showFeedback, tournaments = [], onPoolsChanged }) {
    // Pool Config owns its own tournament + pool selection so it doesn't
    // depend on the Admin-level header dropdowns (which are hidden for this tab).
    const [activeTournament, setActiveTournament] = useState(null);
    const [pools,        setPools]        = useState([]);
    const [activePool,   setActivePool]   = useState(null);
    const [rounds,       setRounds]       = useState([]);
    const [scoringRules, setScoringRules] = useState({});
    const [section,      setSection]      = useState('pools');

    const [newName,      setNewName]      = useState('');
    const [newType,      setNewType]      = useState('bracket');
    const [newScoring,   setNewScoring]   = useState('flat');
    const [newPaired,    setNewPaired]    = useState(false);

    // Default the tournament to the first one once the list arrives
    useEffect(() => {
        if (tournaments.length > 0 && !activeTournament) {
            setActiveTournament(tournaments[0]);
        }
    }, [tournaments, activeTournament]);

    useEffect(() => {
        if (!activeTournament) return;
        getPools(activeTournament.id).then(res => {
            setPools(res.data);
            if (res.data.length > 0) setActivePool(res.data[0]);
            else setActivePool(null);
        });
        getRounds(activeTournament.id).then(res => setRounds(res.data));
    }, [activeTournament]);

    useEffect(() => {
        if (!activePool || rounds.length === 0) return;
        const initial = {};
        rounds.forEach(r => {
            initial[r.id] = { points_per_correct: 1, round_multiplier: 1 };
        });
        getScoringRules(activePool.id).then(res => {
            if (res.data.length > 0) {
                res.data.forEach(rule => {
                    initial[rule.round_id] = {
                        points_per_correct: rule.points_per_correct,
                        round_multiplier:   rule.round_multiplier
                    };
                });
            }
            setScoringRules(initial);
        }).catch(() => setScoringRules(initial));
    }, [activePool, rounds]);

    const handleCreatePool = async (e) => {
        e.preventDefault();
        try {
            const res = await createPool({
                tournament_id:       activeTournament.id,
                name:                newName,
                pool_type:           newType,
                scoring_type:        newScoring,
                force_paired_rounds: newPaired
            });
            showFeedback('Pool created successfully');
            setNewName('');
            const updated = await getPools(activeTournament.id);
            setPools(updated.data);
            setActivePool(res.data);
            // Notify parent so the Admin-level pool dropdown (on other tabs) refreshes
            if (onPoolsChanged) onPoolsChanged();
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to create pool', 'error');
        }
    };

    const handleUpdatePool = async (field, value) => {
        try {
            await updatePoolSettings(activePool.id, { [field]: value });
            showFeedback('Pool updated');
            const updated = await getPools(activeTournament.id);
            setPools(updated.data);
            setActivePool(updated.data.find(p => p.id === activePool.id));
            if (onPoolsChanged) onPoolsChanged();
        } catch (err) {
            showFeedback('Failed to update pool', 'error');
        }
    };

    const handleSaveScoringRules = async () => {
        try {
            const rules = Object.entries(scoringRules).map(([round_id, vals]) => ({
                round_id:           parseInt(round_id),
                points_per_correct: parseInt(vals.points_per_correct),
                round_multiplier:   parseInt(vals.round_multiplier)
            }));
            await updateScoringRules(activePool.id, rules);
            showFeedback('Scoring rules saved');
        } catch (err) {
            showFeedback('Failed to save scoring rules', 'error');
        }
    };

    return (
        <div>
            {/* Self-contained selectors — replaces the hidden Admin-level header dropdowns */}
            <div className="flex items-end gap-4 flex-wrap mb-6">
                <div className="flex flex-col gap-1">
                    <span className="text-xs text-gray-500 uppercase tracking-wide">Tournament</span>
                    <select
                        value={activeTournament?.id || ''}
                        onChange={e => setActiveTournament(tournaments.find(t => t.id === parseInt(e.target.value)))}
                        className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500">
                        {tournaments.length === 0 && <option value="">No tournaments</option>}
                        {tournaments.map(t => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                    </select>
                </div>
                <div className="flex flex-col gap-1">
                    <span className="text-xs text-gray-500 uppercase tracking-wide">Pool</span>
                    <select
                        value={activePool?.id || ''}
                        onChange={e => setActivePool(pools.find(p => p.id === parseInt(e.target.value)))}
                        className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500">
                        {pools.length === 0 && <option value="">No pools</option>}
                        {pools.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="flex gap-2 mb-6">
                {[{ id: 'pools', label: 'Pools' }, { id: 'scoring', label: 'Scoring Rules' }].map(s => (
                    <button key={s.id} onClick={() => setSection(s.id)}
                        className={'px-4 py-2 rounded-lg text-sm font-bold transition-colors ' +
                            (section === s.id ? 'bg-orange-500 text-white' : 'bg-gray-800 text-gray-400 hover:text-white')}>
                        {s.label}
                    </button>
                ))}
            </div>

            {section === 'pools' && (
                <div className="space-y-6">
                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
                        <h3 className="text-lg font-black text-white mb-4">Create new pool</h3>
                        <form onSubmit={handleCreatePool} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="sm:col-span-2">
                                <label className="block text-xs text-gray-400 mb-1">Pool name</label>
                                <input type="text" value={newName} onChange={e => setNewName(e.target.value)}
                                    placeholder="2027 Bracket Pool"
                                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500"
                                    required />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">Pool type</label>
                                <select value={newType} onChange={e => setNewType(e.target.value)}
                                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500">
                                    {POOL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">Scoring type</label>
                                <select value={newScoring} onChange={e => setNewScoring(e.target.value)}
                                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500">
                                    {SCORING_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div className="flex items-center gap-3">
                                <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                                    <input type="checkbox" checked={newPaired}
                                        onChange={e => setNewPaired(e.target.checked)}
                                        className="w-4 h-4" />
                                    Force paired rounds
                                </label>
                            </div>
                            <div className="sm:col-span-2">
                                <button type="submit"
                                    className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-2 rounded-lg transition-colors text-sm">
                                    Create pool
                                </button>
                            </div>
                        </form>
                    </div>

                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
                        <h3 className="text-lg font-black text-white mb-4">Existing pools</h3>
                        <div className="space-y-3">
                            {pools.map(p => (
                                <div key={p.id} className={'p-4 rounded-lg border ' +
                                    (activePool?.id === p.id ? 'border-orange-500 bg-gray-800' : 'border-gray-800 bg-gray-900')}>
                                    <div className="flex items-center justify-between mb-2">
                                        <div>
                                            <p className="text-white font-bold text-sm">{p.name}</p>
                                            <p className="text-gray-500 text-xs">{p.pool_type} — {p.scoring_type} — paired: {p.force_paired_rounds ? 'yes' : 'no'}</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button onClick={() => setActivePool(p)}
                                                className="bg-gray-700 hover:bg-gray-600 text-white text-xs px-3 py-1 rounded transition-colors">
                                                Select
                                            </button>
                                        </div>
                                    </div>
                                    {activePool?.id === p.id && (
                                        <div className="mt-3 pt-3 border-t border-gray-700 grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-xs text-gray-400 mb-1">Pool type</label>
                                                <select
                                                    value={p.pool_type}
                                                    onChange={e => handleUpdatePool('pool_type', e.target.value)}
                                                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500">
                                                    {POOL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-xs text-gray-400 mb-1">Scoring type</label>
                                                <select
                                                    value={p.scoring_type}
                                                    onChange={e => handleUpdatePool('scoring_type', e.target.value)}
                                                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500">
                                                    {SCORING_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                                </select>
                                            </div>
                                            <div className="col-span-2 flex items-center gap-3">
                                                <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                                                    <input type="checkbox"
                                                        checked={p.force_paired_rounds || false}
                                                        onChange={e => handleUpdatePool('force_paired_rounds', e.target.checked)}
                                                        className="w-4 h-4" />
                                                    Force paired rounds
                                                </label>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {section === 'scoring' && (
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h3 className="text-lg font-black text-white">Scoring rules</h3>
                            <p className="text-gray-500 text-sm">{activePool?.name || 'Select a pool'}</p>
                        </div>
                    </div>

                    <div className="space-y-3 mb-6">
                        {rounds.map(r => (
                            <div key={r.id} className="flex items-center gap-4 bg-gray-800 rounded-lg px-4 py-3">
                                <span className="text-white font-bold text-sm w-32">{r.round_name}</span>
                                <div className="flex items-center gap-2">
                                    <label className="text-xs text-gray-400">Points</label>
                                    <input type="number" min="0"
                                        value={scoringRules[r.id]?.points_per_correct ?? 1}
                                        onChange={e => setScoringRules(prev => ({
                                            ...prev,
                                            [r.id]: { ...prev[r.id], points_per_correct: e.target.value }
                                        }))}
                                        className="w-16 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm text-center focus:outline-none focus:border-orange-500"
                                    />
                                </div>
                                <div className="flex items-center gap-2">
                                    <label className="text-xs text-gray-400">Multiplier</label>
                                    <input type="number" min="1"
                                        value={scoringRules[r.id]?.round_multiplier ?? 1}
                                        onChange={e => setScoringRules(prev => ({
                                            ...prev,
                                            [r.id]: { ...prev[r.id], round_multiplier: e.target.value }
                                        }))}
                                        className="w-16 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm text-center focus:outline-none focus:border-orange-500"
                                    />
                                </div>
                                <span className="text-orange-400 text-sm font-black ml-auto">
                                    = {(scoringRules[r.id]?.points_per_correct || 1) * (scoringRules[r.id]?.round_multiplier || 1)} pts
                                </span>
                            </div>
                        ))}
                    </div>

                    <button onClick={handleSaveScoringRules}
                        className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-2 rounded-lg transition-colors text-sm">
                        Save scoring rules
                    </button>
                </div>
            )}
        </div>
    );
}

export default AdminPoolConfig;
